<template>
  <r-ai-page-container :current-step="3">
    <v-row class="text-start">
      <v-col cols="12">
        <div class="pb-4 text-body-1">
          {{ textIntro }}
        </div>
        <div>
          <r-ai-bubble>
            <div v-if="isEditing" class="text-body-2 text--secondary pt-2 pb-2">
              Click X to remove a skill, or add new skills below.
            </div>
            <v-chip
              v-for="skill in hardSkills"
              :key="skill.id"
              class="ma-2 ai-chip"
              color="#dbeafe"
              :close="isEditing"
              @click:close="removeSkill(skill.id)"
            >
              {{ skill.text }}
            </v-chip>
            <div v-if="isEditing" class="pt-2">
              <v-divider></v-divider>
              <div class="pt-4 pb-1 text-body-1">Add New Skill:</div>
              <!-- PR-AGENT-UX: Lightcast-backed autocomplete (vs the bare
                   text field that came before). Same backend endpoint the
                   main HardSkills editor uses — type 2+ chars to see
                   matches, or free-text anything that isn't in the
                   taxonomy. Removes Michael's "one-at-a-time tedious"
                   complaint by making the input forgiving + suggestive. -->
              <div class="d-flex">
                <v-combobox
                  v-model="newSkill"
                  :items="hardSkillSuggestions"
                  :search-input.sync="hardSkillSearch"
                  hide-no-data
                  hide-selected
                  hide-details
                  outlined
                  dense
                  clearable
                  placeholder="Type a skill (e.g. Python, Strategic Planning)"
                  @keyup.enter="addSkill"
                ></v-combobox>
                <v-btn
                  class="ml-2 white-text"
                  color="#00a63e"
                  @click="addSkill"
                >
                  Add
                </v-btn>
              </div>
            </div>
          </r-ai-bubble>
          <div class="pt-2">
            <div v-if="isEditing">
              <v-btn
                class="mt-2 mb-2"
                color="primary"
                outlined
                @click="save()"
                block
              >
                Save and Continue
              </v-btn>
            </div>
            <div v-else>
              <v-btn class="my-4" color="primary" @click="next()" block>
                Looks Good
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="edit()"
                block
              >
                Edit or Add Info
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="saveAndExitToDone"
                block
              >
                Save and Exit Agent
              </v-btn>
              <!-- PR-AGENT-ESCAPE: secondary link to the main hard-skills
                   editor. For users who'd rather use the regular UI than
                   continue the guided flow. -->
              <div class="text-center mt-3">
                <a
                  href="#"
                  class="text-caption text--secondary"
                  @click.prevent="$router.push('/hardskills')"
                >
                  Switch to manual entry
                </a>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </r-ai-page-container>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { mapActions } from "vuex";
import * as UUID from "../../utility/guid";
import { Get } from "../../api/ApiBase";

export default {
  name: "AITechSkills",
  components: {},
  data: () => ({
    textIntro: "Here's what I found for your technical skills:",
    isEditing: false,
    newSkill: "",
    // PR-AGENT-UX: Lightcast-backed autocomplete for the "Add New Skill"
    // combobox. Debounced fetch on input; results merged into v-combobox
    // items. Free-text still works.
    hardSkillSearch: "",
    hardSkillSuggestions: [],
    _hardSkillDebounce: null
  }),
  watch: {
    hardSkillSearch(val) {
      if (!val || val.length < 2) {
        this.hardSkillSuggestions = [];
        return;
      }
      if (this._hardSkillDebounce) clearTimeout(this._hardSkillDebounce);
      var self = this;
      this._hardSkillDebounce = setTimeout(function() {
        self.fetchSkillSuggestions(val);
      }, 250);
    }
  },
  beforeDestroy() {
    if (this._hardSkillDebounce) clearTimeout(this._hardSkillDebounce);
  },
  methods: {
    async fetchSkillSuggestions(prefix) {
      try {
        var resp = await Get(
          "/api/SkillAutocomplete?prefix=" +
            encodeURIComponent(prefix) +
            "&type=hard"
        );
        if (
          resp &&
          resp.success &&
          resp.data &&
          Array.isArray(resp.data.results)
        ) {
          this.hardSkillSuggestions = resp.data.results
            .map(r => r && r.canonicalName)
            .filter(n => !!n);
        }
      } catch (e) {
        this.hardSkillSuggestions = [];
      }
    },
    next() {
      this.$router.push({ path: "/AILeadershipSkills" });
    },
    async saveAndExitToDone() {
      try {
        await this.saveHardSkills();
      } catch (e) {
        console.warn("saveAndExitToDone failed:", e);
      }
      this.$router.push({ path: "/AIDone" });
    },
    edit() {
      this.isEditing = true;
    },
    save() {
      this.saveHardSkills();
      this.next();
    },
    ...mapActions("hardSkill", ["saveHardSkills"]),
    addSkill() {
      if (this.newSkill) {
        this.hardSkills.push({ id: UUID.gen(), text: this.newSkill, level: 2 });
        this.newSkill = "";
        this.hardSkillSearch = "";
        this.hardSkillSuggestions = [];
      }
    },
    removeSkill(id) {
      this.hardSkills = this.hardSkills.filter(skill => skill.id !== id);
    }
  },
  computed: {
    ...mapFields("hardSkill", [
      "hardSkills",
      "hardSkillsDisplay",
      "hardSkillLabels",
      "baseHardSkills"
    ])
  }
};
</script>

<style scoped>
.edit-bubble {
  background-color: #f9fafb;
  border-radius: 10px;
  padding: 16px;
}

.add-bubble {
  background-color: #eff6ff;
  border-radius: 10px;
  padding: 16px;
}
</style>
